import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "../../components/ui/Header";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Icon from "../../components/AppIcon";

import ProviderFormModal from "./components/ProviderFormModal";
import ProviderViewModal from "./components/ProviderViewModal";
import DeleteConfirmModal from "../users-crud-management/components/DeleteConfirmModal";

import {
  getWorkshopsService,
  createWorkshopService,
  updateWorkshopService,
  deleteWorkshopService,
  getTagsService,
  getWorkshopByIdService,
} from "../../redux/services/workshops_service";
import { getAllgovernoratesService } from "redux/services/general_service";
import { toast } from "react-toastify";
import ProviderCrudTable from "./components/ProviderCrudTable";

const EmergencyServiceProvidersCrud = ({ queryParams = {} }) => {
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);

  const { workshopsList, metaWorkshops } = useSelector(
    (state) => state.workshops,
  );

  const [language, setLanguage] = useState("ar");
  const [searchQuery, setSearchQuery] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [formMode, setFormMode] = useState("add");

  /* ---------------- Load Language & Workshops ---------------- */

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language") || "ar";
    setLanguage(savedLanguage);

    dispatch(getWorkshopsService({ page, search: searchQuery, emergency: 1 }));
    dispatch(getAllgovernoratesService());
    dispatch(getTagsService());
  }, [dispatch, page, searchQuery]);

  /* ---------------- Labels ---------------- */
  const labels = {
    title: { en: "Workshops Management", ar: "إدارة ورش الطوارئ" },
    addNew: { en: "Add New Workshop", ar: "إضافة ورشة طوارئ جديدة" },
    search: { en: "Search workshops...", ar: "البحث عن الورش..." },
    noResults: { en: "No workshops found", ar: "لم يتم العثور على ورش" },
  };

  /* ---------------- Handlers ---------------- */
  const handleAddWorkshop = () => {
    setFormMode("add");
    setSelectedWorkshop(null);
    setIsFormModalOpen(true);
  };

  const handleEditWorkshop = (workshop) => {
    setFormMode("edit");
    setSelectedWorkshop(workshop);
    setIsFormModalOpen(true);
  };

  const handleViewWorkshop = (workshop) => {
    setSelectedWorkshop(workshop);
    setIsViewModalOpen(true);
  };

  const handleDeleteWorkshop = (workshop) => {
    setSelectedWorkshop(workshop);
    setIsDeleteModalOpen(true);
  };

  const handleSaveWorkshop = (data) => {
    if (formMode === "add") {
      dispatch(createWorkshopService(data)).then((response) => {
        if (
          response?.payload?.status === 200 ||
          response?.payload?.status === 201
        ) {
          toast.success("تمت عملية الإضافة بنجاح!");
          dispatch(getWorkshopsService({ page , emergency: 1}));
          setSelectedWorkshop(null);
          setIsFormModalOpen(false);
        } else {
          Object.values(response.payload.response?.data?.errors)
            .flatMap((errorArray) => errorArray)
            .forEach((error) => toast.warn(error));
        }
      });
    } else {
      dispatch(
        updateWorkshopService({
          id: selectedWorkshop.id,
          formData: data,
        }),
      ).then((response) => {
        if (
          response?.payload?.status === 200 ||
          response?.payload?.status === 201
        ) {
          toast.success("تم التحديث بنجاح!");
          dispatch(getWorkshopsService({ page , emergency: 1}));
          setSelectedWorkshop(null);
          setIsFormModalOpen(false);
        } else {
          Object.values(response.payload.response?.data?.errors)
            .flatMap((errorArray) => errorArray)
            .forEach((error) => toast.warn(error));
        }
      });
    }
  };

  const handleConfirmDelete = () => {
    dispatch(deleteWorkshopService(selectedWorkshop.id)).then((response) => {
      if (response.payload.status === 200 || response.payload.status === 204) {
        toast("تم الحذف بنجاح");
        setIsDeleteModalOpen(false);
      }
    });
  };

  /* ---------------- Filtering ---------------- */
  const filteredWorkshops = workshopsList?.filter((w) => {
    return (
      w?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w?.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w?.area_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {labels.title[language]}
          </h1>
        </div>

        <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                placeholder={labels.search[language]}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  dispatch(
                    getWorkshopsService({
                      search: e.target.value,
                      emergency: 1,
                    }),
                  );
                  setPage(1);
                }}
                className="lg:max-w-md"
              />
            </div>
            <Button
              onClick={handleAddWorkshop}
              iconName="Plus"
              variant="default"
            >
              {labels.addNew[language]}
            </Button>
          </div>

          {/* Table */}
          <ProviderCrudTable
            workshops={workshopsList}
            metaWorkshops={metaWorkshops}
            language={language}
            onPageChange={(p) => setPage(p)}
            onView={(w) => {
              setSelectedWorkshop(w);
              setIsViewModalOpen(true);
            }}
            onEdit={(w) => {
              setSelectedWorkshop(w);
              setFormMode("edit");
              setIsFormModalOpen(true);
            }}
            onDelete={(w) => {
              setSelectedWorkshop(w);
              setIsDeleteModalOpen(true);
              dispatch(getWorkshopByIdService(w?.id))
            }}
          />

          {filteredWorkshops?.length === 0 ? (
            <div className="text-center py-12">
              <Icon
                name="Wrench"
                size={48}
                className="mx-auto text-muted-foreground mb-4"
              />
              <p className="text-text-secondary">
                {labels.noResults[language]}
              </p>
            </div>
          ) : (
            <ProviderFormModal
              workshops={filteredWorkshops}
              language={language}
              onEdit={handleEditWorkshop}
              onDelete={handleDeleteWorkshop}
              onView={handleViewWorkshop}
            />
          )}
        </div>
      </div>

      {isFormModalOpen && (
        <ProviderFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSave={handleSaveWorkshop}
          workshop={selectedWorkshop}
          mode={formMode}
          language={language}
        />
      )}

      {isViewModalOpen && (
        <ProviderViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          workshop={selectedWorkshop}
          language={language}
          onEdit={() => {
            setIsViewModalOpen(false);
            handleEditWorkshop(selectedWorkshop);
          }}
          onDelete={() => {
            setIsViewModalOpen(false);
            handleDeleteWorkshop(selectedWorkshop);
          }}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          itemName={selectedWorkshop?.name}
          language={language}
        />
      )}
    </div>
  );
};

export default EmergencyServiceProvidersCrud;
